import { z } from "zod";
import { ADMIN_ROLES } from "./roles.js";
import { DEFAULT_VERTICAL_KEY, competencyIdSchema, verticalKeySchema } from "./verticals.js";
import { DEFAULT_INTERVIEW_LANGUAGE, INTERVIEW_LANGUAGE_CODES, interviewLanguageCodeSchema } from "./languages.js";
import { cvScreenSchema } from "./cvScreen.js";
/** zod schemas are the single source of cross-boundary validation (api validates, web infers). */
/**
 * 0043 email identity: EVERY email that crosses the boundary is canonicalised to
 * lower(btrim()) BEFORE validation, because the database now refuses any other form
 * (users_email_canonical) and "Bob@X.com" vs "bob@x.com " must never become two people.
 * One schema, swapped into every email field — never a bare `z.string().email()` again.
 */
export const emailSchema = z.string().trim().toLowerCase().email();
export const regionSchema = z.enum(["in-central", "us-east", "eu-west"]);
export const levelSchema = z.enum(["L1", "L2", "L3"]);
export const entryModeSchema = z.enum(["post", "manual_upload", "direct_candidate", "live_assist"]);
/** Currencies a requirement's budget can be stated in (#237). INR/USD were the only two before; the
 *  rest are additive, so every config written earlier still parses. */
export const SALARY_CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
/** Who does what on a requirement (#237): sourcing — "us" = TalentHop sources (Mode A, a sourcing
 *  order), "client" = the customer brings candidates; interviewing — "us" = TalentHop AI voice
 *  interview, "client" = the customer interviews themselves. Absent = "client" / "us", today's default. */
export const OWNERSHIP_OPTIONS = ["us", "client"];
export const ownershipSchema = z.enum(OWNERSHIP_OPTIONS);
/**
 * Competency selections are validated in TWO halves, on purpose. Shape is checked here — a
 * snake_case id, nothing more — because the set of legal ids depends on the requisition's vertical
 * and only the API can see that. MEMBERSHIP ("is this id one of this vertical's competencies?") is
 * a runtime check the API owns; a closed enum here is exactly what made the platform
 * software-only in the first place.
 */
/**
 * Resume-tailoring policy (own-model). The recruiter reviews this ONCE on the requisition — the
 * shared "spine" of what every candidate is asked stays comparable, while these directives steer
 * HOW each candidate's resume-tailored probes are generated. `focuses` are preset levers; `note`
 * is a free-text steer. Both optional so every existing config still parses.
 */
export const tailoringFocusSchema = z.enum([
    "probe_gaps",
    "challenge_claims",
    "recent_project",
    "career_transitions",
    "leadership_scope",
    "domain_depth",
    "failed_delivery",
    "commercial_vendor",
    "production_incidents",
    "system_tradeoffs",
]);
export const tailoringPolicySchema = z.object({
    // 6 universal + at most 2 vertical extras visible at once; 8 keeps every UI-reachable pick valid.
    focuses: z.array(tailoringFocusSchema).max(8).optional(),
    note: z.string().max(600).optional(),
});
/** Body for PATCH /requisitions/{id}/tailoring — persist the reviewed policy without regenerating. */
export const setTailoringSchema = z.object({
    tailoringPolicy: tailoringPolicySchema.optional(),
});
/** #43: the two proctoring presets. Two, not a matrix of toggles — a preset is explainable to a
 *  candidate at consent and comparable across requisitions; ten independent switches are neither. */
export const INTERVIEW_MODES = ["natural", "strict"];
export const interviewModeSchema = z.enum(INTERVIEW_MODES);
/** The single home of the default. A config from before #43 (or one that never chose) is a
 *  NATURAL interview — strict is always an explicit, disclosed choice, never an accident. */
export function interviewModeOf(config) {
    return config?.interviewMode ?? "natural";
}
/** Recruiter weights over a competency selection: competencyId → 1..100 (whole numbers). */
export const competencyWeightsSchema = z.record(competencyIdSchema, z.number().int().min(1).max(100));
export const workflowConfigSchema = z.object({
    entryMode: entryModeSchema,
    // Defaulted rather than required so a config written before verticals existed still parses —
    // and it defaults to the industry-neutral floor, never to software.
    verticalKey: verticalKeySchema.default(DEFAULT_VERTICAL_KEY),
    levels: z.array(levelSchema).min(1),
    triggerHours: z.number().int().min(1).max(168).default(24),
    applicationCap: z.number().int().min(1).max(5000).default(200),
    screenLimit: z.number().int().min(1).max(5000).default(200),
    interviewTarget: z.number().int().min(1).max(500).optional(), // Mode A: how many to interview
    // Mode A (we-source) marker. Set true when the requisition is a sourcing request our team
    // fulfils MANUALLY — the worker must never auto-post it to boards or simulate applicants.
    sourcing: z.boolean().optional(),
    finalistCount: z.number().int().min(1).max(50).default(5),
    rejectionPolicy: z.enum(["manual_gated", "auto_notify"]).default("manual_gated"),
    // Wave 2: comp context (candidate-facing offer info) — all optional so existing configs still
    // parse AND the inferred type stays optional (matches WorkflowConfig `?` fields). Semantic
    // defaults (currency INR, period year, mode fixed) are applied by consumers via `??`.
    salaryMin: z.number().nonnegative().optional(),
    salaryMax: z.number().nonnegative().optional(),
    salaryCurrency: z.enum(SALARY_CURRENCIES).optional(),
    salaryPeriod: z.enum(["year", "month"]).optional(),
    benefits: z.array(z.string().min(1).max(80)).max(12).optional(),
    // Wave 2: flexible collection window.
    collectionMode: z.enum(["fixed", "flexible"]).optional(),
    minQualified: z.number().int().min(1).max(500).optional(),
    // Wave 3: staff-augmentation engagement context. All optional so FTE configs still parse;
    // the api enforces projectDurationMonths + minRateInr presence when engagementType === "staff_aug".
    engagementType: z.enum(["fte", "staff_aug"]).optional(),
    projectDurationMonths: z.number().int().min(1).max(60).optional(),
    minRateInr: z.number().int().min(0).optional(),
    // Which of the vertical's competencies drive interview depth + cost. ABSENT = nothing selected
    // yet (the recruiter picks them when selecting who to interview); an explicit `[]` is REFUSED,
    // because "assess this person against nothing" is never what anyone means. The UI has always
    // gated it — the API had no minimum, so a scripted POST could store a requisition that generates
    // to a bare "tell me about yourself" and then judges every rubric line off it.
    // The API additionally checks each id is actually in `verticalKey`; this only checks the shape.
    competencies: z.array(competencyIdSchema).min(1).max(12).optional(),
    // How much each selected competency counts in the composite (6 Sep 2026). Keys are competency
    // ids of `competencies`; values are whole numbers 1-100, stored exactly as the recruiter typed
    // them — the arithmetic normalises (packages/shared/src/scoring.ts), the screen shows the
    // resulting percentages. ABSENT = the vertical edition's own weights, which is every requisition
    // written before this field existed. The API checks the keys match `competencies` exactly
    // (competencyWeightsProblem); this only checks the shape. Recruiters may change these at any
    // time via PATCH /requisitions/{id}/weights — every candidate re-ranks on the next read and the
    // change lands on the requisition timeline (see scoring.ts for why that is the honest choice).
    competencyWeights: competencyWeightsSchema.optional(),
    // New Request v2: staff-aug engagements price the resource hourly, not by annual salary.
    hourlyRateInr: z.number().int().min(0).optional(),
    // Own-model: how per-candidate resume-tailored questions are steered (reviewed once here).
    tailoringPolicy: tailoringPolicySchema.optional(),
    // #43: proctoring preset. Optional so every config written before it existed still parses —
    // absent means "natural", today's advisory behaviour, and `interviewModeOf` is the ONE place
    // that default lives. "strict" gates the interview on camera + fullscreen, pauses (never
    // terminates) on violations, disables paste, measures speaking-time attention, and weights
    // presence at STRICT_PRESENCE_WEIGHT. Disclosed to the candidate at consent, always.
    interviewMode: interviewModeSchema.optional(),
    // WS4: which languages the candidate may choose to be interviewed in (Sep 2026). Optional so
    // every config written before it existed still parses — ABSENT = ["en"], today's behaviour, and
    // `interviewLanguagesOf` is the ONE place that default lives. The API additionally checks each
    // code is enabled AND consent-reviewed in `interview_languages` (a language ships dark until an
    // owner flips both); this only checks the shape. The candidate picks ONE of these at consent.
    interviewLanguages: z.array(interviewLanguageCodeSchema).min(1).max(INTERVIEW_LANGUAGE_CODES.length).optional(),
    // Live adaptive follow-up questions — optional so every config written before this switch
    // existed still parses as "on" (absent = true, today's behaviour). Explicit `false` turns them
    // off on both the live candidate path and the worker's simulated-interview path.
    adaptiveChallengesEnabled: z.boolean().optional(),
    // Resume-tailored question plans per candidate (0097) — absent = on (today's behaviour). Explicit
    // `false` = every candidate is asked the shared requirement plan. Forced off on the free plan.
    resumeQuestionsEnabled: z.boolean().optional(),
    // What happens when a candidate's CV arrives (cvScreen.ts) — absent = off, today's behaviour.
    cvScreen: cvScreenSchema.optional(),
    // Customer portal rich spec & calibration parameters (stored as JSON in config)
    department: z.string().optional(),
    targetExperience: z.string().optional(),
    location: z.string().optional(),
    workplaceMode: z.string().optional(),
    skills: z.array(z.string()).optional(),
    requiredSkills: z.array(z.string()).optional(),
    bonusSkills: z.array(z.string()).optional(),
    visa: z.string().optional(),
    noticePeriod: z.string().optional(),
    summary: z.string().optional(),
    // #237: the review step states the place as country + city (location stays "City, Country"),
    // and records who interviews (see OWNERSHIP_OPTIONS). Optional — older configs still parse.
    country: z.string().max(80).optional(),
    city: z.string().max(80).optional(),
    interviewOwner: ownershipSchema.optional(),
    // #238 "Other criteria": extra requirements saved with the requirement (visa sponsorship, a
    // notice period when it matters, anything else). Stored and shown; not scored.
    otherCriteria: z.array(z.object({ label: z.string().min(1).max(60), value: z.string().min(1).max(200) })).max(12).optional(),
    budgetMin: z.union([z.string(), z.number()]).optional(),
    budgetMax: z.union([z.string(), z.number()]).optional(),
}).passthrough();
/** The single home of the default. A config from before WS4 (or one that never chose) offers
 *  English only — a second language is always an explicit recruiter choice, never an accident. */
export function interviewLanguagesOf(config) {
    const chosen = config?.interviewLanguages;
    return chosen && chosen.length > 0 ? [...chosen] : [DEFAULT_INTERVIEW_LANGUAGE];
}
/** Wave 3: shared org-kind enum. createOrgSchema lives in api-orgs; it extends its local schema
 *  with `kind: orgKindSchema.default("end_customer")`. */
export const orgKindSchema = z.enum(["end_customer", "vendor", "platform"]);
/**
 * 0041: mark a NEW organisation a test org. Creation-only, deliberately — there is no update
 * schema anywhere that accepts this field, and the database refuses the flip regardless (trigger
 * organizations_is_test_immutable), so the two layers agree instead of the API being the only
 * thing standing between a live client and a guessable candidate code.
 */
export const orgIsTestSchema = z.boolean().default(false);
/** Wave 3: a recruiter assigns an end_customer's staff_aug requisition to a vendor org. */
export const assignVendorSchema = z.object({
    vendorOrgId: z.string().uuid(),
});
/** Wave 3: a platform admin links a vendor org to an end_customer (org_relationships). */
export const linkVendorSchema = z.object({
    vendorOrgId: z.string().uuid(),
    scope: z.record(z.unknown()).optional(),
});
export const createRequisitionSchema = z.object({
    title: z.string().min(2).max(200),
    jobDescription: z.string().max(20000).optional(),
    config: workflowConfigSchema,
});
/**
 * PATCH /requisitions/{id} — the only requisition fields a recruiter can change after creation
 * without touching what a candidate is scored against: the label and the brief. Everything else
 * (vertical, competencies, entry mode) either has its own guarded endpoint (see
 * requisitionsRepoint) or has no edit path at all, deliberately.
 */
export const updateRequisitionSchema = z
    .object({
    title: z.string().min(2).max(200).optional(),
    jobDescription: z.string().max(20000).optional(),
    config: z.record(z.unknown()).optional(),
})
    .refine((v) => v.title !== undefined || v.jobDescription !== undefined || v.config !== undefined, {
    message: "Provide title, jobDescription, and/or config to update",
});
export const candidateContactSchema = z.object({
    name: z.string().min(1).max(160),
    email: emailSchema.optional(),
    phone: z.string().min(6).max(20).optional(),
}).refine((c) => c.email || c.phone, { message: "email or phone is required" });
export const consentGrantSchema = z.object({
    purposes: z.array(z.enum([
        "interview_capture",
        "biometric_liveness",
        "aadhaar_verify",
        "model_training",
        "persona_reuse_per_org",
        "cross_border_access",
        "raw_media_transfer",
    ])).min(1),
    noticeVersion: z.string(),
    // WS4: the language the candidate chose to be interviewed in — recorded on the consent row
    // (consents.notice_locale) and on the candidacy. Absent = "en", which is every consent granted
    // before this field existed. The API refuses a code the requisition does not offer.
    language: interviewLanguageCodeSchema.optional(),
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(8).max(200),
});
export const inviteUserSchema = z.object({
    email: emailSchema,
    role: z.enum([
        "super_admin",
        "recruiter",
        "hiring_manager",
        "interviewer",
        "reviewer",
        "billing_admin",
        "external_client",
    ]),
});
/** Owner "act as company" — policy-gated + reason required + audited. */
export const impersonateSchema = z.object({
    orgId: z.string().uuid(),
    reason: z.string().min(4).max(500),
    durationMinutes: z.number().int().min(5).max(60).default(30),
});
export const addCandidatesSchema = z.object({
    candidates: z.array(candidateContactSchema).min(1).max(200),
});
/** Candidate import (Excel/CSV): which of these addresses are already on the requirement? */
export const checkCandidateEmailsSchema = z.object({
    emails: z.array(emailSchema).min(1).max(1000),
});
/**
 * Candidate import, "request their details" path: add each person WITHOUT an interview (free —
 * nothing is scored or invited) and email them the "complete your application" link. The interview
 * path reuses addCandidatesSchema / POST /requisitions/{id}/candidates, which bills per interview.
 */
export const importCandidatesSchema = z.object({
    candidates: z
        .array(z.object({
        name: z.string().trim().min(1).max(160),
        email: emailSchema.max(320),
        // What the recruiter's file says about the person (6 Oct 2026). All optional, all context:
        // kept on recruiter_details, shown to the team, never scored or gated on.
        phone: z.string().trim().max(40).optional(),
        experienceYears: z.number().min(0).max(60).optional(),
        noticePeriodDays: z.number().int().min(0).max(365).optional(),
        lastWorkingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
        currentCtc: z.string().trim().max(60).optional(),
        expectedCtc: z.string().trim().max(60).optional(),
        location: z.string().trim().max(120).optional(),
        extra: z.record(z.string().max(60), z.string().max(300)).optional(),
    }))
        .min(1)
        .max(200),
    needsResume: z.boolean().default(true),
    needsProfile: z.boolean().default(true),
    /** Original file name, kept as the candidacy's source_detail so the team can see where they came from. */
    sourceFile: z.string().trim().max(300).optional(),
    /** Optional: set this requirement's CV screen in the same step (see cvScreen.ts). */
    cvScreen: cvScreenSchema.optional(),
});
/** New Request v2: recruiter uploads resumes (base64) to be AI-scored against the requirement.
 *  Bounded to 20 per request — each one is a multimodal LLM call, so the batch is kept small. */
export const resumeUploadSchema = z.object({
    resumes: z
        .array(z.object({
        filename: z.string().min(1).max(400),
        mime: z.string().min(1).max(200),
        // 10 MB per resume (QA #12) — base64 inflates 4/3, so ~14M chars. The client blocks
        // oversize files with a friendly count; this is the backstop for direct API callers.
        contentBase64: z.string().min(1).max(14_000_000),
    }))
        .min(1)
        .max(20),
    // When true, register the resumes WITHOUT AI scoring (no scan fee): the recruiter just wants to
    // pick candidates to interview themselves. Contact (email) is parsed from the resume text; there
    // is no fit score. Free — no payment context is required in this mode.
    skipScoring: z.boolean().optional(),
});
/** New Request v2: recruiter picks which scored resumes to interview (checkboxes) and which of
 *  the vertical's competencies to assess. The selection is written onto the requisition config. */
export const selectForInterviewSchema = z.object({
    scanIds: z.array(z.string().uuid()).min(1),
    competencies: z.array(competencyIdSchema).min(1).max(12),
    // Optional per-competency weights over `competencies` (keys must match it exactly — the API
    // checks). Absent = keep whatever the requisition already carries, so an older client that does
    // not send weights never wipes ones a recruiter set on the detail page.
    competencyWeights: competencyWeightsSchema.optional(),
    // Optional email overrides keyed by scanId — used when a resume had no email so the recruiter
    // filled one in. When present (or already on the scan), the invite is EMAILED automatically.
    contacts: z.record(emailSchema).optional(),
});
/** Mode A (sourcing-as-a-service): the recruiter provides the requirement + how many resumes to
 *  source & screen, how many to interview, and how many finalists to recommend. We charge for the
 *  screening + interviewing service; our team sources the candidates behind the scenes. */
export const sourcingSchema = z.object({
    // Mode A package minimums (2026-07-27, founder call): one posting = at least 40 resumes
    // screened and 20 interviews, with exactly 3 recommended finalists — the requestor hires any
    // one of the 3. Every resume + every interview summary is shared (transparency contract).
    screenCount: z.number().int().min(40).max(5000),
    interviewCount: z.number().int().min(20).max(500),
    finalistCount: z.literal(3),
    competencies: z.array(competencyIdSchema).min(1).max(12),
});
/** New Request v2: persist the chosen competencies onto a requisition BEFORE the question plan is
 *  generated (fixes the "only HR populated" timing bug) and/or ask the server to (re)generate the
 *  plan from an explicit selection. Both fields optional. */
export const setCompetenciesSchema = z.object({
    competencies: z.array(competencyIdSchema).max(12).optional(),
    // Persisted onto config alongside the competencies (same rules as selectForInterviewSchema).
    competencyWeights: competencyWeightsSchema.optional(),
    // Persisted onto config alongside the competencies so the review-step tailoring policy sticks.
    tailoringPolicy: tailoringPolicySchema.optional(),
});
/** PATCH /requisitions/{id}/weights — re-weight an existing selection. `null` clears the
 *  recruiter's weights and returns the requisition to the vertical edition's own. */
export const setWeightsSchema = z.object({
    competencyWeights: competencyWeightsSchema.nullable(),
});
/** PATCH /requisitions/{id}/languages — replace the offered set. Never empty: "no language" is not
 *  a thing an interview can run in, so the way to go back to English-only is `["en"]`. */
export const setInterviewLanguagesSchema = z.object({
    interviewLanguages: z.array(interviewLanguageCodeSchema).min(1).max(INTERVIEW_LANGUAGE_CODES.length),
});
/** PATCH /admin/interview-languages/{code} — the two owner switches. Both optional so the console
 *  can flip one without restating the other; the API refuses to disable English. */
export const adminInterviewLanguageSchema = z.object({
    enabled: z.boolean().optional(),
    consentReviewed: z.boolean().optional(),
});
export const scheduleSchema = z
    .object({
    slot: z.string().datetime().optional(),
    tz: z.string().min(1).max(64).optional(), // IANA zone the candidate picked the slot in
    startNow: z.boolean().optional(), // async interview — no reason to make them wait
})
    .refine((s) => s.startNow === true || typeof s.slot === "string", {
    message: "Provide a slot, or set startNow",
});
/** Candidate tells us where to send their interview link (stored; sent when messaging goes live). */
export const inviteContactSchema = z.object({
    email: emailSchema,
});
export const answerSchema = z.object({
    questionId: z.string().uuid(),
    answerText: z.string().max(20000).optional(),
    code: z
        .object({
        language: z.string().min(1).max(40),
        source: z.string().min(1).max(20000),
    })
        .optional(),
    durationSeconds: z.number().int().min(0).optional(),
    // Snippet choice (#78): which option the candidate picked on a choose-then-explain question.
    // Optional so a question without options — and an older client mid-deploy — still submits;
    // correctness is computed server-side against the stored correct_index, never trusted from here.
    pickedIndex: z.number().int().min(0).max(3).optional(),
    // World-class pass: how the answer was given + whether the candidate revealed the text.
    // "voice_edited" (0063): spoken, then hand-corrected in the here's-what-I-heard box before
    // submitting. Distinct from plain voice so a report (and the capture-loss check against the
    // recording) can say which words were the candidate's edit rather than the engine's hearing.
    answeredBy: z.enum(["typed", "voice", "voice_edited"]).optional(),
    textRevealed: z.boolean().optional(),
});
/** Government ID kinds accepted at check-in — mirrors IdType in types.ts. */
export const idTypeSchema = z.enum([
    "aadhaar_digilocker",
    "pan",
    "driving_license",
    "passport",
    "voter_id",
]);
/** Identity-verification providers the seam knows how to route to. 'sim' is the default,
 *  honest simulation; the rest are real-vendor adapters that stay `pending_integration`
 *  until their credentials are configured and their adapter is wired (Package B / POC). */
export const idvProviderSchema = z.enum([
    "sim",
    "idfy",
    "hyperverge",
    "persona",
    "sumsub",
    "iproov",
]);
// The canonical `IdvProviderId` type lives in types.ts; this schema validates the same set.
/** Which candidate-facing flow proves identity — govt-ID/liveness, or a lighter email OTP. */
export const idVerificationMethodSchema = z.enum(["id_document", "otp"]);
/** platform_settings `id_verification` config (PUT /admin/providers/id-verification). */
export const idVerificationConfigSchema = z.object({
    enabled: z.boolean(),
    provider: idvProviderSchema,
    // Defaults to the original flow so existing stored configs (written before this field
    // existed) parse the same as they always have.
    method: idVerificationMethodSchema.default("id_document"),
    allowedIdTypes: z.array(idTypeSchema).min(1),
    apiKey: z.string().max(400).optional(),
    // Some vendors need more than a key: an account/tenant id, a base URL, and which
    // endpoint tier to hit. All optional so 'sim' and simple providers ignore them.
    accountId: z.string().max(200).optional(),
    baseUrl: z.string().url().max(400).optional(),
    mode: z.enum(["sandbox", "production"]).optional(),
});
/** Candidate check-in. `idType` is used for the id_document method; `code` for the otp
 *  method. Both are optional here — the handler enforces which one is required, based on
 *  the platform's configured method, since neither applies when verification is OFF. */
export const checkinSchema = z.object({
    idType: idTypeSchema.optional(),
    code: z.string().length(6).optional(),
});
/** Candidate · Step 1 info form (POST /invite/{token}/info). Name/email are editable
 *  pre-consent so a stale or mistyped invite address can be corrected before OTP send;
 *  LinkedIn is always optional and never required to proceed. */
export const candidateInfoSchema = z.object({
    candidateName: z.string().trim().min(1).max(200).optional(),
    email: emailSchema.max(320).optional(),
    linkedinUrl: z.string().trim().url().max(500).optional().or(z.literal("")),
});
/** Asha "rephrase" — ask the judge for a simpler wording of the current question. */
export const rephraseSchema = z.object({
    questionId: z.string().uuid(),
});
/**
 * v2 Stage 3 — tailored, recruiter-editable interview questions (requisition_question_plan).
 *
 * A question plan is generated from the requisition's job description + the selected competencies
 * of its vertical (and, when available, a resume), then reviewed/edited/added-to by the recruiter
 * before candidates are interviewed. `competencyId` is the question's PLANNING TARGET — the
 * criterion it is designed to draw evidence for, one of the requirement's assessment set (checked by
 * the API). It is NOT a scoring mapping: since 0099 the evaluator reads every answer against every
 * criterion and decides itself which answers evidence what. `kind` is the question shape the interview UI
 * renders. The interview runtime maps these into interview_questions (behavioral stays behavioral;
 * every other kind is served as a verbal technical question because a plan carries no code
 * spec/snippet) and falls back to the vertical's own competency questions when no plan exists.
 */
/**
 * Matches a self-introduction opener, whether we minted it or the model wrote its own. The
 * generator uses it to find the opener it must keep untargeted and provenance-free
 * (questiongen.normaliseOpeners). The opener is an ordinary scored question — never match it live
 * per-render to style it differently: prompts are editable, and a marker that appears/disappears
 * mid-keystroke reparents the question's DOM and drops focus.
 */
export const INTRO_PROMPT_RE = /(tell me (a bit )?about yourself|about yourself|walk me through your background)/i;
export const plannedQuestionSchema = z.object({
    id: z.string(),
    /**
     * OPTIONAL planning target (0100): the criterion this question is designed to draw evidence for.
     * Never what it is scored on — the evaluator reads every answer against every criterion. Null or
     * absent = untargeted, which is always valid: a question needs no competency to be asked, and
     * removing a competency from the criteria never invalidates a plan.
     */
    competencyId: competencyIdSchema.nullable().optional(),
    prompt: z.string().min(4).max(1000),
    // "skillset"/"deep" are retired from GENERATION (#18 vocabulary hygiene) but stay parseable
    // here: stored plans from before the retirement carry them, and a strict enum would 400 a
    // recruiter's first edit of an old plan. They normalise to "technical" on read — which is
    // exactly how the interview always served them anyway.
    kind: z.preprocess((v) => (v === "skillset" || v === "deep" ? "technical" : v), z.enum(["behavioral", "technical", "code", "code_reading"])),
    /**
     * DEPRECATED - no longer produced or shown. The warm opener used to be flagged "rapport, not
     * evidence"; it is now an ordinary scored question (its answer reaches the judge like any other).
     * Kept optional so plans stored before that still parse; a stored `true` is still written to
     * interview_questions with a NULL competency_id (a harmless, untargeted row), and the editor
     * drops the flag on the next save.
     */
    unscored: z.boolean().optional(),
    /**
     * QUESTION PROVENANCE — where this question was drawn from: the requisition's job description
     * ("requirement"), THIS candidate's resume ("resume"), or the vertical's own question set
     * ("standard"). Both fields are OPTIONAL because every plan stored before this wave has
     * neither, and absence must keep parsing; consumers render absence as "no provenance", never
     * as a claim. Optional also matters on the PUT path: zod strips undeclared fields, so without
     * these declarations the recruiter's first edit would silently wipe provenance off every
     * question (the exact failure mode `unscored` was declared to prevent).
     *
     * A "requirement"/"resume" tag is only ever stored VERIFIED: questiongen checks that
     * `sourcePhrase` genuinely appears in the claimed document (case-insensitive,
     * whitespace-normalised) and downgrades to "standard" with the phrase dropped when it does
     * not. A fabricated "from your requirement: …" in a client report is worse than no provenance.
     */
    source: z.enum(["requirement", "resume", "standard"]).optional(),
    /** The verbatim phrase the question was drawn from — only alongside a verified source tag. */
    sourcePhrase: z.string().min(3).max(160).optional(),
});
/** The full editable set a recruiter saves for a requisition (bounded so one call can't bloat). */
export const questionPlanSchema = z.object({
    questions: z.array(plannedQuestionSchema).max(40),
});
/** The engine ranks; a human decides. A reject ALWAYS carries a reason. */
export const decisionSchema = z
    .object({
    action: z.enum(["advance", "reject"]),
    reason: z.string().min(4).max(1000).optional(),
    override: z.boolean().optional(),
})
    .refine((d) => d.action !== "reject" || !!d.reason, {
    message: "reason is required when rejecting",
    path: ["reason"],
});
/** Requester closes out a finished requisition; reason is optional context for the audit trail. */
export const closeRequisitionSchema = z.object({
    reason: z.string().max(500).optional(),
});
export const noteSchema = z.object({
    body: z.string().min(1).max(4000),
});
/** Recruiter conduct observations (0107): what a person noticed that the browser could not. */
export const OBSERVATION_CATEGORIES = ["reading_script", "another_person", "another_screen", "tech_issue", "other"];
export const observationSchema = z.object({
    category: z.enum(OBSERVATION_CATEGORIES),
    note: z.string().trim().min(1).max(1000),
    /** Seconds into the recording, when the recruiter knows. */
    atSeconds: z.number().int().min(0).max(86_400).optional(),
});
export const shareSchema = z.object({
    recipientEmail: emailSchema.optional(),
    redacted: z.boolean().default(true),
});
export const boardMappingSchema = z.object({
    region: regionSchema,
    board: z.string().min(2).max(60),
    enabled: z.boolean().default(true),
    priority: z.number().int().min(0).max(100).default(0),
});
export const flagSchema = z.object({
    enabled: z.boolean(),
});
export const modelUpsertSchema = z.object({
    // A verticals.key: calibration weights are looked up by it, so a free-text label here would be
    // a row nothing can ever find (which is exactly what the old scope names were).
    vertical: verticalKeySchema,
    name: z.string().min(2).max(120),
    version: z.string().min(1).max(40),
    status: z.enum(["candidate", "active", "retired"]),
    metrics: z.record(z.unknown()).default({}),
});
export const mfaVerifySchema = z.object({
    code: z.string().regex(/^\d{6}$/, "6-digit code"),
});
// Derived from ADMIN_ROLES, never re-typed: the hand-copied literal here is what silently
// rejected every 'analyst' provisioning with "Invalid admin payload" while the console offered
// the option and the DB constraint allowed it.
export const provisionAdminSchema = z.object({
    email: emailSchema,
    name: z.string().min(1).max(160),
    role: z.enum(ADMIN_ROLES),
});
export const provisionOrgAdminSchema = z.object({
    email: emailSchema,
    name: z.string().min(1).max(160),
});
export const dsrSchema = z.object({
    kind: z.enum(["export", "erase"]),
});
/**
 * Candidate lost their interview link and asks for a fresh one. Email is the ONLY input —
 * there are no candidate accounts and no password; the address is matched against the
 * invite we already hold, never used as a send target on its own.
 */
export const recoverLinkSchema = z.object({
    email: emailSchema,
});
/**
 * Browser-integrity proctoring events (candidate side). These are FLAGS for a human
 * reviewer — never an auto-decision and never a "personality"/character score. The
 * candidate UI batches signals (tab away, paste, fullscreen exit, extra/absent face)
 * and posts them; the recruiter report surfaces the counts + a recent tail so a person
 * can judge context. `at` is the client-supplied ISO timestamp of the event; `detail`
 * is a short, optional free-text hint (e.g. how many faces were seen), capped so a
 * hostile client can't bloat a row.
 *
 * ONE VOCABULARY, THREE CONSUMERS — the emitter (useProctoring), this validator, and the
 * presence scorer. They drifted once and it cost us three of the five scored signals: the
 * scorer penalised `tab_away`/`absent_face`/`extra_face`, names no client has ever emitted
 * and this schema would have rejected, so those events were stored and shown to the reviewer
 * but scored zero. The list below is the single source of truth, and the penalty map is keyed
 * off it so a new kind cannot be added without the compiler demanding its weight.
 *
 * THREE OF THESE COST NOTHING AND ARE NOT OFFENCES — they are the bookkeeping that makes the
 * rest READABLE, and without them "we saw nothing" is indistinguishable from "there was nothing
 * to see":
 *   • `proctor_on`     — the integrity channel came up on this device. Its ABSENCE is what tells
 *                        the scorer it has no data at all (→ presence null, not a near-perfect 92).
 *   • `face_watch_on`  — a real face detector produced a real reading from the camera at least
 *                        once. Only then may a report say camera presence was observed.
 *   • `face_visible`   — the RETURN half of `no_face`/`second_face`, carrying how long the
 *                        preceding state lasted, so a reviewer sees a duration, not a bare count.
 */
export const INTEGRITY_EVENT_KINDS = [
    "proctor_on",
    "tab_blur",
    "tab_focus",
    "paste",
    "fullscreen_exit",
    "face_watch_on",
    "second_face",
    "no_face",
    "face_visible",
    // ── Strict-mode additions (#43). Same contract as the camera trio: *_watch_on is the proof a
    // detector genuinely ran, findings are inadmissible without it (see GAZE_DERIVED in integrity.ts).
    "gaze_watch_on", // a gaze detector produced a real reading from a real frame (detail: engine)
    "gaze_away", // sustained off-screen attention WHILE SPEAKING (detail: left|right|down)
    "gaze_return", // the return receipt (detail: "away:<seconds>") — coming back costs nothing
    "paste_blocked", // strict mode swallowed a paste (detail: length) — prevented, so zero penalty
    "strict_pause", // interview paused by strict enforcement (detail: no_face|fullscreen_exit|camera_lost)
    "strict_resume", // pause ended (detail: "<reason>:<seconds paused>")
    "strict_bypass", // candidate used the escape hatch to continue despite an unmet requirement (detail: reason)
];
export const integrityEventSchema = z.object({
    kind: z.enum(INTEGRITY_EVENT_KINDS),
    at: z.string(),
    detail: z.string().max(200).optional(),
});
/** A posted batch of integrity events — bounded so one request can't flood the table. */
export const integrityBatchSchema = z.object({
    events: z.array(integrityEventSchema).max(500),
});
/**
 * Per-occurrence presence penalty, subtracted from the observational presence figure the report
 * shows (and, when the Owner switch is on, weighted into the overall by applyPresence). The
 * ladder is by how strongly a signal suggests the candidate was somewhere else — or someone
 * else: an extra face outranks an empty frame, which outranks a tab switch. `tab_focus` is the
 * RETURN half of `tab_blur`; coming back is not a second offence, so it costs nothing — and the
 * same goes for `face_visible`, the return half of the two camera signals.
 *
 * `proctor_on` / `face_watch_on` are capability heartbeats, not behaviour: a candidate must never
 * lose a point because their browser was able to tell us more than someone else's.
 */
export const INTEGRITY_PRESENCE_PENALTY = {
    proctor_on: 0,
    tab_blur: 6,
    tab_focus: 0,
    paste: 4,
    fullscreen_exit: 6,
    face_watch_on: 0,
    second_face: 14,
    no_face: 10,
    face_visible: 0,
    gaze_watch_on: 0,
    // Deliberately the smallest behavioural penalty on the ladder. Gaze estimation is the least
    // reliable signal we hold — head pose reads differently across faces, glasses, lighting and
    // neurodivergent gaze patterns — so an episode costs less than a tab switch, is capped by the
    // same episode debounce, and the report presents it as an attention OBSERVATION for a human,
    // never as evidence of cheating.
    gaze_away: 4,
    gaze_return: 0,
    paste_blocked: 0, // the paste never landed; the flag is context, not an offence
    strict_pause: 0, // the CAUSE (no_face / fullscreen_exit) is already penalised — never twice
    strict_resume: 0,
    strict_bypass: 0, // a broken webcam must not cost points; the reviewer sees it in the timeline
};
// ── Secure auth onboarding (0013) ─────────────────────────────────────────────
// Self-service auth: invite set-password links, forgot-password reset links, and
// email-OTP MFA / email verification. Email only — no phone/SMS anywhere.
/** Set (or reset) a password from a one-time link. `token` is the RAW 64-hex link token;
 *  the api resolves it by sha256 hash. Min length mirrors loginSchema (8). */
export const setPasswordSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(8).max(200),
});
/** Forgot-password. NON-ENUMERATING: the api always answers 200 {ok:true} whether or not
 *  the address exists — email is the only input and is never echoed back. */
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
/** Kick off email verification. `email` omitted ⇒ verify the account's current address. */
export const verifyEmailSendSchema = z.object({
    email: emailSchema.optional(),
});
/** Confirm a 6-digit email OTP (email verification or email-MFA management). */
export const verifyCodeSchema = z.object({
    code: z.string().length(6),
});
/** Re-issue an org admin's account invite (set_password link) — doubles as a resend. */
export const resendInviteSchema = z.object({
    email: emailSchema,
});
/**
 * Which invite / entry modes are available platform-wide. Stored as platform_settings
 * key 'invite_modes'. A disabled mode is rejected at requisition creation. DEFAULT when
 * the row is absent: direct_candidate + manual_upload on, post + live_assist off — the
 * two pilot-ready paths on, the two still-maturing paths off.
 */
export const inviteModesSchema = z.object({
    direct_candidate: z.boolean(),
    manual_upload: z.boolean(),
    post: z.boolean(),
    live_assist: z.boolean(),
});
// ── Self-serve onboarding (0069) ────────────────────────────────────────────────
// Public signup: free (30-credit starter, admin-approved) or paid (credit pack, auto-approved
// on payment). `credits` — not a packId — is what the client sends for the paid path; the
// server resolves it against the active credit_packs catalog, so no pack id is a public input.
export const SIGNUP_FREE_CREDITS = 30;
export const signupSchema = z
    .object({
    orgName: z.string().trim().min(2).max(200),
    name: z.string().trim().min(1).max(120),
    email: emailSchema,
    password: z.string().min(8).max(200),
    plan: z.enum(["free", "paid"]),
    /** PAID: the plan bought at signup (0097) — priced by quotePlan, like the in-app checkout. */
    planId: z.string().uuid().optional(),
    quantity: z.number().int().min(1).max(50).optional(),
    /** Legacy pricing-page links (?plan=paid&credits=N) — no longer sold; ignored by the server. */
    credits: z.number().int().positive().optional(),
    /** The applicant ticked "I agree to the Terms and Privacy Policy". */
    acceptTerms: z.literal(true),
})
    .refine((d) => d.plan === "free" || d.planId != null, {
    message: "Choose a plan",
    path: ["planId"],
});
/** The FREE plan a self-serve signup gets on approval (platform_settings 'free_plan', 0097). */
export const freePlanSchema = z.object({
    jobs: z.number().int().min(0).max(100),
    resumes: z.number().int().min(0).max(10_000),
    interviews: z.number().int().min(0).max(1_000),
    validityDays: z.number().int().min(1).max(365),
});
/** Seed values — the owner can change the row; code falls back to these when it's missing/invalid. */
export const DEFAULT_FREE_PLAN = { jobs: 1, resumes: 50, interviews: 5, validityDays: 14 };
/** Confirm a signup's email from the one-time link (`POST /auth/verify-signup`). */
export const verifySignupSchema = z.object({
    token: z.string().min(1),
});
/**
 * POST /candidacies/{id}/request-info — the recruiter asking a candidate for what's missing.
 *
 * Both `needsResume` and `needsProfile` default true: the overwhelmingly common case is an
 * application that arrived with neither, and a recruiter pressing "Request CV" should not have
 * to specify what they want before anything is sent.
 */
export const requestCandidateInfoSchema = z.object({
    needsResume: z.boolean().default(true),
    needsProfile: z.boolean().default(true),
    /** Shown to the candidate above the form; also available to the email as {{recruiter_note}}. */
    note: z.string().trim().max(500).optional(),
    /** Days the link stays usable. A fortnight by default — long enough to survive a holiday. */
    expiresInDays: z.number().int().min(1).max(60).default(14),
});
/**
 * POST /candidate-profile/{token} — what the candidate submits on their own page.
 *
 * Every field is optional on its own, but the handler refuses a submission that carries nothing
 * at all: an empty POST would mark the request complete and stop the reminder, having collected
 * nothing. The resume rides as base64 like every other upload path in this codebase.
 */
export const candidateProfileSubmitSchema = z.object({
    candidateName: z.string().trim().min(1).max(200).optional(),
    email: emailSchema.max(320).optional(),
    phone: z.string().trim().max(40).optional(),
    linkedinUrl: z.string().trim().max(500).optional(),
    location: z.string().trim().max(160).optional(),
    /** Whole years. Optional — a fresher legitimately answers 0. */
    experienceYears: z.number().int().min(0).max(60).optional(),
    noticePeriodDays: z.number().int().min(0).max(365).optional(),
    /** Optional detail fields (0090, candidateFields.ts) — stored only when the job asks for them. */
    currentCtc: z.string().trim().max(60).optional(),
    expectedCtc: z.string().trim().max(60).optional(),
    visaNeeded: z.boolean().optional(),
    /** The candidate's own words and background — self-reported, never scored. */
    about: z.string().trim().max(600).optional(),
    skills: z.array(z.string().trim().min(1).max(40)).max(15).optional(),
    graduationYear: z.number().int().min(1960).max(2040).optional(),
    /** Their last day at their current job (YYYY-MM-DD) — asked when they can join immediately. */
    lastWorkingDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "Use the date format YYYY-MM-DD")
        .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), "Enter a real date")
        .optional(),
    resume: z
        .object({
        filename: z.string().trim().min(1).max(300),
        mime: z.string().trim().min(1).max(200),
        contentBase64: z.string().min(1),
    })
        .optional(),
});
//# sourceMappingURL=schemas.js.map